import { useState, useEffect, useMemo, useContext } from "react";
import { useLocation } from "react-router-dom";
import { API, graphqlOperation } from "aws-amplify";
import { captureError } from "../utilities";
import {
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Grid,
  Typography,
  Snackbar,
  Box,
  CircularProgress,
} from "@mui/material";
import { Alert, LoadingButton } from "@mui/lab";
import { upsertReview } from "../graphql/mutations";
import { getReviewState } from "../graphql/queries";
import OrderSummary from "../components/OrderSummary";
import { getCompany } from "../graphql/client-queries";
import { calculateDiscountedPrice } from "../shared/pricing-utilities";
import { AppContext } from "../contextProviders/AppContext";
import SendIcon from "@mui/icons-material/Send";

function ReviewPage() {
  const query = new URLSearchParams(useLocation().search);
  const reservationId = query.get("reservationId");
  const [rating, setRating] = useState();
  const [ratingError, setRatingError] = useState();
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("error");
  const [reviewState, setReviewState] = useState("");

  const {
    cartTotalValue,
    replaceCart,
    setSelectedAdditionalServices,
    setReservationStart,
    setReservationReturn,
    setSelectedCompany,
  } = useContext(AppContext);

  const [submittedReservation, setSubmittedReservation] = useState(null);
  const [appliedVoucher, setAppliedVoucher] = useState(null);

  const discountedTotalPrice = useMemo(() => {
    const paymentStatus = submittedReservation?.paymentStatus;
    // If deposit has been paid but waiting for the rest, show the payment todo as total price.
    if (
      paymentStatus === "COMPLETED_DEPOSIT" ||
      paymentStatus === "PENDING_FULL"
    ) {
      return submittedReservation.paymentData.paymentTodo;
    }

    return calculateDiscountedPrice(
      cartTotalValue,
      appliedVoucher?.amount || 0
    );
  }, [cartTotalValue, appliedVoucher, submittedReservation]);

  const fetchCompany = async (companyId) => {
    const response = await API.graphql(
      graphqlOperation(getCompany, {
        id: companyId,
      })
    );
    setSelectedCompany(response.data.getCompany);
  };

  async function fetchReview() {
    try {
      const response = await API.graphql(
        graphqlOperation(getReviewState, {
          reservationId: reservationId,
        })
      );
      const state = response.data.getReviewState.state;
      setReviewState(state);
      // Set Reservation information
      if (reservationId && state === "REVIEW_PENDING") {
        const reservation = response.data.getReviewState.reservationInfo;
        setSubmittedReservation(reservation);
        if (reservation.voucherData) {
          setAppliedVoucher(reservation.voucherData);
        }
        const newCart = reservation.vehicles.map((v) => {
          return {
            quantity: 1,
            productPrice: v.price,
            offerPrice: v.price,
            vehicle: {
              ...v,
            },
          };
        });
        replaceCart(newCart);
        const services = reservation.services.map((s) => {
          return {
            ...s,
            offerPrice: s.price,
          };
        });
        setSelectedAdditionalServices(services);
        fetchCompany(reservation.companyId);
        setReservationStart(new Date(reservation.startTime));
        setReservationReturn(new Date(reservation.returnTime));
      }
      setLoading(false);
      return state;
    } catch (e) {
      handleOpen("Jokin meni vikaan.", "info");
      console.error("error fetching review", e);
      setLoading(false);
      throw new Error("Failed to fetch review.");
    }
  }

  const SpinnerLoader = () => {
    return (
      <Box
        marginTop="40dvh"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Grid container direction="column" alignItems="center">
          <Grid item>
            <CircularProgress />
          </Grid>
          <Grid item>
            <Box mt={2}>Lataa...</Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  useEffect(() => {
    fetchReview();
  }, []);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleOpen = (message, severity) => {
    setMessage(message);
    setSeverity(severity);
    setOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!rating && rating !== 0) {
      handleOpen("Valitse arvostelu.", "warning");
      return;
    }
    setSubmitting(true);
    setRatingError("");

    try {
      const response = await API.graphql(
        graphqlOperation(upsertReview, {
          review: { reservationId, rating, comment },
        })
      );
      setReviewState("REVIEW_DONE");
    } catch (e) {
      captureError("Upsert review failed", "UPSERT_REVIEW_FAILED", e);
      handleOpen("Jokin meni vikaan.", "info");
    }
    setSubmitting(false);
  };

  const RedGreenRadio = (props) => (
    <Radio
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
      }}
      color="default"
      {...props}
    />
  );

  const RedGreenFormControlLabel = (props) => (
    <FormControlLabel
      sx={{
        marginLeft: 0,
        marginRight: 0,
        "& span": {
          fontSize: 18,
          fontWeight: "bold",
        },
        label: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        },
      }}
      {...props}
    />
  );

  function getButtonColor(value) {
    const hue = Math.floor((value / 10) * 120);
    const color = `hsl(${hue}, 100%, 50%)`;
    return color;
  }

  function RedGreenRadioButtons() {
    const buttons = [];
    for (let i = 0; i <= 10; i++) {
      const color = getButtonColor(i);
      const isLeft = i === 0;
      const isRight = i === 10;
      const buttonStyle = {
        backgroundColor: isLeft ? "red" : isRight ? "green" : color,
        fontSize: 24,
        fontWeight: "bold",
        width: 40,
        height: 40,
        borderRadius: "50%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      };
      buttons.push(
        <Grid item xs={2} sm={1} key={i}>
          <RedGreenFormControlLabel
            value={i}
            control={
              <RedGreenRadio checked={i === rating} value={i.toString()} />
            }
            label={
              <div
                style={{
                  backgroundColor: buttonStyle.backgroundColor,
                  ...buttonStyle,
                }}
              >
                {i}
              </div>
            }
            labelPlacement="top"
          />
        </Grid>
      );
    }

    return (
      <RadioGroup
        row
        value={rating}
        onChange={(event, value) => setRating(parseInt(value))}
      >
        <Grid container justifyContent="center" spacing={2}>
          {buttons}
        </Grid>
      </RadioGroup>
    );
  }

  return (
    <>
      {loading ? (
        <SpinnerLoader />
      ) : (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ height: "100vh" }}
        >
          <Snackbar
            open={open}
            autoHideDuration={6000}
            onClose={handleClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={handleClose}
              severity={severity}
              sx={{ width: "100%" }}
            >
              {message}
            </Alert>
          </Snackbar>
          {reviewState === "REVIEW_EXPIRED" && (
            <Grid item xs={12} md={8} style={{ marginTop: "40dvh" }}>
              <Typography variant="h4" component="h1" align="center">
                Tälle varaukselle ei voi enää antaa palautetta!
              </Typography>
            </Grid>
          )}
          {reviewState === "REVIEW_DONE" ||
          reviewState === "PENDING_EXPLANATION" ? (
            <Grid item xs={12} md={8} style={{ marginTop: "40dvh" }}>
              <Typography variant="h4" component="h1" align="center">
                Kiitos palautteesta!
              </Typography>
              <Typography variant="h5" component="h4" align="center">
                Voit nyt sulkea sivun.
              </Typography>
            </Grid>
          ) : null}
          {reviewState === "REVIEW_PENDING" && (
            <form onSubmit={handleSubmit}>
              <Grid item xs={12}>
                <OrderSummary
                  discountedTotalPrice={discountedTotalPrice}
                  showPrice={false}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  align="center"
                  style={{
                    marginBottom: "3vh",
                    marginTop: "5vh",
                    maxWidth: "95%",
                    margin: "auto",
                  }}
                >
                  Kuinka todennäköisesti suosittelisit palvelua ystävällesi?
                </Typography>
                <RedGreenRadioButtons />
                {ratingError && (
                  <Typography color="error">{ratingError}</Typography>
                )}
              </Grid>
              <Grid
                container
                justifyContent="center"
                style={{ marginTop: "20px" }}
              >
                <TextField
                  label="Kommentti"
                  style={{ maxWidth: "90%" }}
                  multiline
                  fullWidth
                  variant="outlined"
                  rows={5}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                />
              </Grid>
              <Grid
                item
                xs={12}
                container
                justifyContent="center"
                style={{ marginTop: "20px" }}
              >
                <LoadingButton
                  color="primary"
                  variant="contained"
                  loading={submitting}
                  loadingPosition="end"
                  endIcon={<SendIcon />}
                  type="submit"
                >
                  Lähetä
                </LoadingButton>
              </Grid>
            </form>
          )}
        </Grid>
      )}
    </>
  );
}

export default ReviewPage;
